import { useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from '@mui/material';
import api from '../services/api';
import { wranglerError } from './errors';
import ImportResultView from './ImportResultView';
import useWranglerJob, { responsesProgressText } from './useWranglerJob';

const progressText = (progress = {}) => {
  if (progress.page != null) return responsesProgressText(progress);
  if (progress.total) return `${progress.stage || 'Reading quotas'}: ${progress.read ?? 0} of ${progress.total}`;
  return progress.stage || 'Waiting for the worker…';
};

/**
 * Copy the survey's Alchemer quotas into Wrangler: confirm, follow the job, show the result.
 * The job syncs the survey's questions itself, first.
 */
const ImportDialog = ({ surveyId, open, onClose, onFinished }) => {
  const [jobId, setJobId] = useState(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const { job, error: jobError } = useWranglerJob(surveyId, jobId);
  const finished = job?.status === 'done' || job?.status === 'failed';

  useEffect(() => {
    if (finished) onFinished?.(job);
    // onFinished is the caller's reload; it runs once per finished job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished]);

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      const { data } = await api.post(`/surveys/${surveyId}/wrangler/import-alchemer/`);
      setJobId(data.job.id);
    } catch (err) {
      setError(wranglerError(err));
    } finally {
      setStarting(false);
    }
  };

  const close = () => {
    onClose();
    if (finished || !jobId) setJobId(null);
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle>Import quotas from Alchemer</DialogTitle>
      <DialogContent>
        {!jobId && (
          <Typography variant="body2">
            Copies this survey&apos;s Alchemer quotas into Wrangler. The copies arrive switched OFF and marked
            Needs review, so nothing changes for respondents until you&apos;ve checked them and switched them ON.
            Alchemer&apos;s own quotas aren&apos;t changed. Quotas imported before are skipped.
          </Typography>
        )}
        {jobId && !finished && !jobError && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2">{progressText(job?.progress)}</Typography>
          </Box>
        )}
        {job?.status === 'failed' && <Alert severity="error">The import failed: {job.error || 'no reason given.'}</Alert>}
        {job?.status === 'done' && <ImportResultView result={job.progress?.result} />}
        {(error || jobError) && <Alert severity="error" sx={{ mt: 2 }}>{error || jobError}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={close} sx={{ textTransform: 'none' }}>{finished ? 'Close' : jobId ? 'Hide' : 'Cancel'}</Button>
        {!jobId && (
          <Button variant="contained" onClick={start} disabled={starting} sx={{ textTransform: 'none' }}>Import</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportDialog;
